import { useState } from 'react'
import { Eye, LocateFixed } from 'lucide-react'
import { useAppStore } from '@core/store'
import { isFeatureEnabled } from '@core/features'
import {
  checkSelectedLineOfSight,
  formatMetres,
  type VisibilityCheckResult,
} from './visibilityClash'

export default function VisibilityCheck() {
  const count = useAppStore((state) => state.selectedTargets.length)
  const [busy, setBusy] = useState(false)
  const [result, setResult] = useState<VisibilityCheckResult | null>(null)
  const [error, setError] = useState<string | null>(null)
  if (!isFeatureEnabled('visibilityClashes')) return null

  const run = async () => {
    setBusy(true)
    setError(null)
    try {
      setResult(await checkSelectedLineOfSight())
    } catch (cause) {
      setResult(null)
      setError(cause instanceof Error ? cause.message : String(cause))
    } finally {
      setBusy(false)
    }
  }

  return (
    <section className="border-b border-[var(--color-border)] px-3 py-2 text-xs">
      <div className="flex items-center gap-2">
        <Eye size={14} className="text-[var(--color-accent)]" />
        <strong>Linha de visão</strong>
        <span className="text-[11px] text-[var(--color-text-muted)]">
          entre os centros de 2 elementos
        </span>
        <button
          type="button"
          className="ml-auto rounded border border-[var(--color-border)] px-2 py-1 hover:bg-[var(--color-surface)] disabled:opacity-50"
          disabled={count !== 2 || busy}
          onClick={() => void run()}
        >
          {busy ? 'A verificar…' : 'Verificar'}
        </button>
      </div>
      {error && <p className="mt-1 text-[var(--color-error-text)]">{error}</p>}
      {result && (
        <div className="mt-2 rounded border border-[var(--color-border)] bg-[var(--color-bg)] p-2">
          <p className={result.clear ? 'text-emerald-400' : 'text-[var(--color-warn-text)]'}>
            {result.clear
              ? `Visibilidade livre ao longo de ${formatMetres(result.distance)}.`
              : `${result.blockers.length} obstáculo(s) em ${formatMetres(result.distance)}.`}
          </p>
          {result.blockers.slice(0, 8).map((blocker) => (
            <button
              key={`${blocker.modelId}:${blocker.localId}`}
              type="button"
              className="mt-1 flex w-full items-center gap-1 rounded px-1 py-0.5 text-left hover:bg-[var(--color-surface)]"
              onClick={() =>
                useAppStore.getState().applyBulkSelection(
                  [{ modelId: blocker.modelId, localId: blocker.localId }],
                  'replace',
                )
              }
            >
              <LocateFixed size={12} />
              #{blocker.localId} a {formatMetres(blocker.distance)}
            </button>
          ))}
        </div>
      )}
    </section>
  )
}

